import {
  CUA_CLOUD_MACHINE_QUERY_KEY,
  cuaCloudClient,
} from '@/app-shell/rpa/cua-cloud/cuaCloud';
import { CUA_CLOUD_FLAG_KEY } from '@/features/flags/desktop-target-selector-flag';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { DESKTOP_TARGETS_QUERY_KEY } from './desktopTargets';

export type ResidentSetup = {
  provisioning: boolean;
  setUp: () => void;
};

// Distinct from CUA_CLOUD_MACHINE_QUERY_KEY: the settings page caches the
// details payload under the base key; this summary probe must not clash.
// UNCARVED DOMAIN KEY (rpa, CUA-44): rpa has no workspace package
export const CUA_CLOUD_MACHINE_SUMMARY_QUERY_KEY = [
  ...CUA_CLOUD_MACHINE_QUERY_KEY,
  'summary',
] as const;

const isNotProvisioned = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

/**
 * Inline "Set up" affordance for the desktop-target picker. CUA Cloud users
 * with no Resident yet get a provision action instead of an empty picker.
 *
 * Returns `undefined` to hide the affordance: flag off, machine state still
 * loading, or a machine already exists — the provision endpoint registers
 * the roster row itself, so an existing machine is already in the picker.
 */
export const useResidentSetup = (enabled: boolean): ResidentSetup | undefined => {
  const cuaCloudEnabled = useBooleanFlagValue(CUA_CLOUD_FLAG_KEY, false);
  const queryClient = useQueryClient();
  const active = enabled && cuaCloudEnabled;

  const machineQuery = useQuery({
    queryKey: CUA_CLOUD_MACHINE_SUMMARY_QUERY_KEY,
    queryFn: async () => (await cuaCloudClient.getMachine()).data,
    enabled: active,
    // 404 = not provisioned yet — the state this hook exists to surface.
    retry: (failureCount, error) => !isNotProvisioned(error) && failureCount < 2,
  });

  const provisionMutation = useMutation({
    mutationFn: () => cuaCloudClient.ensureMachine(),
    onSettled: async () => {
      // Base-key prefix covers both the summary probe and the settings page.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CUA_CLOUD_MACHINE_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: DESKTOP_TARGETS_QUERY_KEY }),
      ]);
    },
  });

  if (!active) return undefined;
  if (provisionMutation.isPending) {
    return { provisioning: true, setUp: () => undefined };
  }
  if (!isNotProvisioned(machineQuery.error)) return undefined;
  return { provisioning: false, setUp: () => provisionMutation.mutate() };
};
